import './BottomSheet.sass';

import React, { FC, useEffect, useRef, useState } from 'react';
import { animated, useSpring } from '@react-spring/web';
import { useDispatch } from 'react-redux';

import { IcClose28 } from '$assets';
import { mainActions } from '$store/main';
import { BottomSheetProps } from './BottomSheet.interface';
import { classNames, classNames as cn } from '$utils';
import clamp from 'lodash/clamp';
import { useGesture } from 'react-use-gesture';
import useSize from '@react-hook/size';
import { useResize } from '$hooks';

const MAX_DRAG_OUT = 40;
const MIN_DRAG_OFFSET = 1.5;

export const BottomSheet: FC<BottomSheetProps> = ({
  children,
  title,
  scroll = true,
  onClose,
  onClosing,
  className,
  closeButtonShown = true,
  dismissible = true,
  asideContent,
  popupId = -1,
  setPopupId,
  isActiveControl = false,
  chain = [],
  closeRef,
}) => {
  const dispatch = useDispatch();
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOpened, setIsOpened] = useState(isActiveControl);
  const [isDragging, setIsDragging] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);
  const [{ y }, api] = useSpring(() => ({ y: 0 }));
  const [{ opacity }, opacityApi] = useSpring(() => ({ opacity: 0 }));
  const [_, contentHeight] = useSize(contentRef);
  const { height } = useResize();
  const isNextPopup = chain.length > 0 && popupId >= 0 && !isAnimating;

  useEffect(() => {
    if (popupId >= 0) {
      api.start({
        y: 0,
        immediate: false,
        onRest: () => {
          setIsAnimating(false);
        },
      });
    }
  }, [popupId]);

  useEffect(() => {
    handleOpen();
  }, []);

  useEffect(() => {
    if (isOpened) {
      api.start({ y: -contentHeight, immediate: false });
    }
  }, [contentHeight, height]);

  useEffect(() => {
    const listener = () => {
      dismissible && onClose && onClose();
    };
    window.addEventListener('popstate', listener, false);
    return () => {
      window.removeEventListener('popstate', listener, false);
    };
  }, [onClose, dismissible]);

  const handleOpen = () => {
    if (!contentRef.current) {
      return;
    }

    api.start({ y: -contentHeight, immediate: false });
    opacityApi.start({ opacity: 1, immediate: false });
    setIsOpened(true);
  };

  const handleClose = () => {
    if (!dismissible) {
      return;
    }
    onClosing?.();
    opacityApi.start({ opacity: 0, immediate: false });
    api.start({
      y: 0,
      onRest: () => {
        if (onClose) {
          onClose();
        } else {
          dispatch(mainActions.setPopup(null));
        }
      },
    });
    setIsOpened(false);
    setPopupId?.(-1);
  };

  const getNextY = (
    down: boolean,
    currentY: number,
    movementY: number,
    velocity: number,
  ) => {
    const nextY = currentY + movementY;

    if (down) {
      return nextY <= currentY - MAX_DRAG_OUT ? currentY - MAX_DRAG_OUT : nextY;
    }

    const threshold = 100 / velocity;

    if (isOpened) {
      return movementY > threshold ? 0 : -contentHeight;
    }

    return contentHeight;
  };

  const bind = useGesture(
    {
      onDrag: ({ down, movement: [_, movementY], velocity, memo = y.get() }) => {
        const currentVelocity = clamp(velocity, 0.5, 8);
        const threshold = 100 / currentVelocity;

        !isDragging && setIsDragging(Math.abs(movementY) >= MIN_DRAG_OFFSET);

        if (!down) {
          if (isOpened && movementY > threshold) {
            handleClose();
          }
          setTimeout(() => setIsDragging(false));
        }

        api({
          y: getNextY(down, memo, movementY, currentVelocity),
          immediate: down,
        });

        return memo;
      },
    },
    {
      drag: {
        enabled: isOpened && dismissible,
      },
    },
  );

  useEffect(() => {
    const escape = (e: KeyboardEvent) => {
      e.key === 'Escape' && handleClose();
    };

    window.addEventListener('keydown', escape, false);
    return () => {
      window.removeEventListener('keydown', escape, false);
    };
  }, [handleClose]);

  if (closeRef) closeRef.current = handleClose;

  return (
    <div className={classNames('BottomSheet', className, { open: isOpened })}>
      <animated.div
        className="BottomSheet__close_helper"
        onClick={(e) => {
          e.stopPropagation();
          !isDragging && handleClose();
        }}
        style={{ opacity, touchAction: isOpened ? 'initial' : 'none' }}
        {...bind()}
      />
      {isNextPopup && chain[popupId]}
      {!!asideContent && asideContent}
      <div className="BottomSheet__sheet_wrap">
        <animated.div className="BottomSheet__sheet" ref={contentRef} style={{ y }}>
          <div className="BottomSheet__header" {...bind()}>
            <div className="BottomSheet__header__title">{title}</div>
            {closeButtonShown && (
              <div
                className="BottomSheet__header__close_wrap"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClose();
                }}
              >
                <div className="BottomSheet__header__close">
                  <IcClose28 />
                </div>
              </div>
            )}
          </div>
          <div className={cn('BottomSheet__content', { hasScroll: scroll })}>
            {children}
          </div>
        </animated.div>
      </div>
    </div>
  );
};
